import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { DetailCta } from "@/components/detail/DetailCta";
import { engagementPhases } from "@/data/engagement.data";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { Check, Clock } from "@/icons";
import { siteConfig } from "@/config/site.config";

export default function Process() {
  return (
    <>
      <Seo
        title="Process"
        description="The complete VibeWell engagement journey — consultation, discovery, planning, design, development, testing, deployment, training and long-term support."
        path="/process"
      />
      <PageHero
        eyebrow="Process"
        titleLines={["How great", "products get made."]}
        description="A transparent, collaborative journey from first conversation to launch and beyond — with visible progress and shared ownership at every step."
      >
        <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
          Start with a Consultation
        </CtaButton>
      </PageHero>

      <Section spacing="compact" className="pt-0">
        <Container>
          <SectionHeading
            eyebrow="The engagement journey"
            title="Nine deliberate phases"
            description="Each phase has clear activities, deliverables and a typical duration — so you always know where we are and what's next."
          />

          <div className="relative mt-14">
            {/* Vertical connective line */}
            <div className="absolute left-[27px] top-2 hidden h-[calc(100%-2rem)] w-px bg-border sm:block" />

            <motion.ol
              variants={staggerContainer(0.08, 0.05)}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="space-y-6"
            >
              {engagementPhases.map(({ index, title, icon: Icon, summary, activities, deliverables, duration }) => (
                <motion.li key={index} variants={fadeUp} className="relative">
                  <div className="grid gap-5 rounded-[1.5rem] border border-border bg-card p-6 sm:grid-cols-[auto_1fr] sm:gap-7 sm:p-8">
                    <div className="flex items-start gap-4 sm:flex-col sm:items-center">
                      <span className="relative z-10 inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[0_12px_30px_-12px_rgba(15,118,110,0.6)]">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="font-mono text-sm text-accent sm:mt-3">{index}</span>
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-heading text-xl font-bold text-foreground">{title}</h3>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" /> {duration}
                        </span>
                      </div>
                      <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{summary}</p>
                      <div className="mt-5 grid gap-5 sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">What happens</p>
                          <ul className="mt-3 space-y-2">
                            {activities.map((a) => (
                              <li key={a} className="flex items-start gap-2 text-sm text-foreground">
                                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                  <Check className="h-2.5 w-2.5" />
                                </span>
                                {a}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">You receive</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {deliverables.map((d) => (
                              <span key={d} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground">
                                {d}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.li>
              ))}
            </motion.ol>
          </div>
        </Container>
      </Section>

      <DetailCta note="Wherever you are on this journey, we'll meet you there. Let's talk." />
    </>
  );
}
